import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, SiteLink, AddToCampaignModal, useOutreach, ConfirmModal } from "../shared";
import { investorsApi, capitalApi } from "../../lib/api";
import { toast } from "sonner";
import { UserPlus, Search, Share2, Trash2 } from "lucide-react";

const TYPES_FALLBACK = ["All"];
const SHOW_ALL = 50000;

export const InvestorListSection = ({ dealKey, dealName, tracked, onImported }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [byType, setByType] = useState({});
  const [q, setQ] = useState("");
  const [type, setType] = useState("All");
  const [eng, setEng] = useState("");
  const [limit, setLimit] = useState(200);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const outreach = useOutreach();

  const load = useCallback(async () => {
    const [list, stats] = await Promise.all([
      investorsApi.list({ ...(type !== "All" ? { type } : {}), ...(q ? { q } : {}), limit }),
      investorsApi.stats(),
    ]);
    setRows(list);
    setTotal(stats.total || 0);
    setByType(stats.byType || {});
  }, [q, type, limit]);

  useEffect(() => { const t = setTimeout(() => load().catch(() => toast.error("Could not load the investor list")), 300); return () => clearTimeout(t); }, [load]);

  const typeOptions = useMemo(() => {
    const entries = Object.entries(byType).sort((a, b) => b[1] - a[1]);
    return entries.length ? ["All", ...entries.map(([t]) => t)] : TYPES_FALLBACK;
  }, [byType]);
  const typeLabel = (t) => (t === "All" ? `All CRM types (${total.toLocaleString()})` : `${t} (${(byType[t] || 0).toLocaleString()})`);
  const matchCount = type === "All" ? total : (byType[type] || 0);
  const trackedEmails = useMemo(() => new Set(tracked.map((l) => (l.email || "").toLowerCase()).filter(Boolean)), [tracked]);
  const visible = useMemo(() => rows.filter((r) => {
    if (eng === "campaign") return r.inCampaign;
    if (eng === "new") return !r.inCampaign;
    return true;
  }), [rows, eng]);
  const picked = visible.filter((r) => sel[r.id]);

  const addSelected = async () => {
    if (!picked.length) return toast.error("Tick at least one contact");
    setBusy("sel");
    try {
      const r = await capitalApi.importInvestors({ dealKey, dealName, ids: picked.map((x) => x.id) });
      setSel({});
      onImported();
      toast.success(`${r.imported} added to the LP pipeline (${r.skipped} already tracked)`);
    } finally { setBusy(""); }
  };
  const addToCampaign = () => {
    const withEmail = picked.filter((r) => r.email);
    if (!withEmail.length) return toast.error("Tick at least one contact with an email address");
    outreach.start(withEmail.map((r) => ({ id: r.id, name: r.name || r.firm, email: r.email, company: r.firm,
      title: r.type || "LP", website: r.website, notes: [r.type, r.notes].filter(Boolean).join(" · ") })),
      `${dealName || "LP"} — ${type === "All" ? "investor" : type} outreach`, "capital");
  };
  const deleteSelected = async () => {
    setBusy("del");
    try {
      const r = await investorsApi.bulkDelete(picked.map((x) => x.id));
      setSel({});
      await load();
      toast.success(`${r.deleted} contact(s) deleted from the investor CRM`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    finally { setBusy(""); }
  };
  const addAllMatching = async () => {
    setBusy("all");
    try {
      const r = await capitalApi.importInvestors({ dealKey, dealName, type: type === "All" ? null : type, limit: 1000 });
      onImported();
      toast.success(`${r.imported} added to the LP pipeline (${r.skipped} already tracked)${r.remaining ? ` — ${r.remaining.toLocaleString()} still queued, press again` : ""}`);
    } finally { setBusy(""); }
  };

  return (
    <Card title={`Investor list — ${total.toLocaleString()} contacts`} testid="cm-investor-list">
      <div className="cm-toolbar">
        <input className="inp cm-search" placeholder="Search name, email, firm…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cil-search" />
        <select className="inp cm-inline" value={type} onChange={(e) => setType(e.target.value)} data-testid="cil-type">
          {typeOptions.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
        </select>
        <select className="inp cm-inline" value={limit} onChange={(e) => setLimit(+e.target.value)} data-testid="cil-limit">
          {[100, 200, 500, 1000].map((n) => <option key={n} value={n}>{`Show ${n}`}</option>)}
          <option value={SHOW_ALL}>{`Show all${matchCount ? ` (${matchCount.toLocaleString()})` : ""}`}</option>
        </select>
        <button className="btn btn-primary" onClick={addSelected} disabled={busy === "sel"} data-testid="cil-add-selected">
          <UserPlus size={13} />{busy === "sel" ? "Adding…" : `Add ${picked.length || ""} to pipeline`}
        </button>
        <button className="btn" onClick={addAllMatching} disabled={busy === "all"} data-testid="cil-add-all">
          <Search size={13} />{busy === "all" ? "Adding…" : `Add next 1,000 ${type === "All" ? "" : type + " "}matching`}
        </button>
        <button className={`btn ${picked.length ? "btn-primary" : ""}`} onClick={addToCampaign} disabled={!picked.length} data-testid="cil-campaign">
          <Share2 size={13} />Add to campaign{picked.length ? ` (${picked.length})` : ""}
        </button>
        <button className="btn btn-danger" onClick={() => setConfirmDel(true)} disabled={!picked.length || busy === "del"} title="Delete the selected contacts from the investor CRM" data-testid="cil-delete">
          <Trash2 size={13} />{busy === "del" ? "Deleting…" : `Delete${picked.length ? ` (${picked.length})` : ""}`}
        </button>
      </div>

      <div className="cm-legend" data-testid="cil-legend">
        <button type="button" className={`cm-legend-chip ${eng === "campaign" ? "on" : ""}`} onClick={() => setEng(eng === "campaign" ? "" : "campaign")} title="Show only investors already in a campaign" data-testid="cil-filter-campaign">
          <span className="lead-name-dot campaign" style={{ position: "static" }} /> In a campaign{eng === "campaign" ? " ✓" : ""}
        </button>
        <button type="button" className={`cm-legend-chip ${eng === "new" ? "on" : ""}`} onClick={() => setEng(eng === "new" ? "" : "new")} title="Show only investors not yet in a campaign" data-testid="cil-filter-new">
          Not in a campaign{eng === "new" ? " ✓" : ""}
        </button>
      </div>

      <div className="cm-table-wrap">
        <table className="tbl inv-tbl" data-testid="cil-table">
          <thead>
            <tr>
              <th><input type="checkbox" checked={visible.length > 0 && visible.every((r) => sel[r.id])}
                onChange={(e) => setSel(e.target.checked ? Object.fromEntries(visible.map((r) => [r.id, true])) : {})} data-testid="cil-check-all" /></th>
              <th>Name</th><th>Email</th><th>Firm</th><th>Type</th><th>Status</th><th>In pipeline</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id} className={`cm-lead-row ${r.inCampaign ? "in-campaign" : ""}`} data-testid={`cil-row-${r.id}`} data-incampaign={r.inCampaign ? "1" : "0"}>
                <td><input type="checkbox" checked={!!sel[r.id]} onChange={() => setSel((s) => ({ ...s, [r.id]: !s[r.id] }))} data-testid={`cil-check-${r.id}`} /></td>
                <td>{r.inCampaign && <span className="lead-name-dot campaign" title={`In campaign${r.campaignName ? `: ${r.campaignName}` : ""}`} style={{ position: "static", marginRight: 5 }} />}{r.name || "—"}</td>
                <td style={{ color: "var(--text2)" }}>{r.email || "—"}</td>
                <td>{r.firm ? <>{r.firm}<SiteLink url={r.website} name={r.firm} testid={`cil-site-${r.id}`} /></> : "—"}</td>
                <td><span className="tag">{r.type || "—"}</span></td>
                <td style={{ color: "var(--text3)", fontSize: 11 }}>{r.status || "New"}</td>
                <td>{trackedEmails.has((r.email || "").toLowerCase()) ? <span className="pill go">Tracked</span> : <span className="cm-sub">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && <div className="cm-empty-block">{eng ? "No contacts match that campaign filter." : "No contacts match that search."}</div>}
      </div>
      <AddToCampaignModal open={outreach.open} onClose={() => outreach.setOpen(false)} contacts={outreach.contacts}
        defaultGroupName={outreach.defaultName} source="capital" dealKey={dealKey} dealName={dealName} />
      <ConfirmModal open={confirmDel} title={`Delete ${picked.length} contact(s)?`}
        message="This removes them from your Investors CRM for good. LPs already added to a deal pipeline are not affected."
        confirmLabel="Delete contacts" onConfirm={() => { setConfirmDel(false); deleteSelected(); }} onCancel={() => setConfirmDel(false)} />
      <div className="cm-empty" style={{ marginTop: 10 }}>Showing {rows.length.toLocaleString()} of {total.toLocaleString()} contacts. Adding a contact creates an LP in this deal's pipeline at stage Prospect — it never edits your Investors CRM record.</div>
    </Card>
  );
};
